import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider } from "@/i18n/I18nProvider";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/portal/ProtectedRoute";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import BlogPage from "@/pages/BlogPage";
import PackagesPage from "@/pages/PackagesPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import VisaPage from "@/pages/VisaPage";
import GenericServicePage from "@/pages/GenericServicePage";
import AdminRequestsPage from "@/pages/AdminRequestsPage";
import ApplyPage from "@/pages/ApplyPage";
import VisaCountriesPage from "@/pages/VisaCountriesPage";
import CanadaApplyPage from "@/pages/CanadaApplyPage";
import PortugalApplyPage from "@/pages/PortugalApplyPage";
import CountryApplyPage from "@/pages/CountryApplyPage";
import DataRequestPage from "@/pages/DataRequestPage";
import ActivatePage from "@/pages/ActivatePage";
import PortalLayout from "@/components/portal/PortalLayout";
import PortalLogin from "@/pages/portal/PortalLogin";
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalProcesses from "@/pages/portal/PortalProcesses";
import PortalProcessDetail from "@/pages/portal/PortalProcessDetail";
import PortalUsers from "@/pages/portal/PortalUsers";
import PortalOrders from "@/pages/portal/PortalOrders";
import PortalApplications from "@/pages/portal/PortalApplications";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalAi from "@/pages/portal/PortalAi";
import PortalSettings from "@/pages/portal/PortalSettings";
import CustomerBilling from "@/pages/account/CustomerBilling";
import AccountLayout from "@/components/account/AccountLayout";
import CustomerLogin from "@/pages/account/CustomerLogin";
import CustomerDashboard from "@/pages/account/CustomerDashboard";
import CustomerProcessDetail from "@/pages/account/CustomerProcessDetail";
import PortalNotifications from "@/pages/portal/PortalNotifications";
import MagicPage from "@/pages/MagicPage";
import ContactPage from "@/pages/ContactPage";
import { ForgotPasswordPage, ResetPasswordPage } from "@/pages/PasswordPages";
import ProfilePage from "@/components/profile/ProfilePage";
import ChatWidget from "@/components/common/ChatWidget";
import PortalSupport from "@/pages/portal/PortalSupport";

function App() {
  return (
    <I18nProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/payment/success" element={<PaymentSuccessPage />} />
          <Route path="/payment/cancel" element={<PaymentCancelPage />} />
          <Route path="/visa" element={<VisaPage />} />
          <Route
            path="/translation"
            element={
              <GenericServicePage
                serviceType="translation"
                i18nKey="translation"
                testIdPrefix="translation"
                fieldLabels={{
                  field1: "Tipo de documento",
                  field2: "Idioma de origem",
                  field3: "Idioma de destino",
                }}
              />
            }
          />
          <Route
            path="/authentication"
            element={
              <GenericServicePage
                serviceType="authentication"
                i18nKey="authentication"
                testIdPrefix="authentication"
                fieldLabels={{
                  field1: "Tipo de documento",
                  field2: "Entidade emissora",
                  field3: "País onde será apresentado",
                }}
              />
            }
          />
          <Route
            path="/passport"
            element={
              <GenericServicePage
                serviceType="passport"
                i18nKey="passport"
                testIdPrefix="passport"
                fieldLabels={{
                  field1: "Tipo de pedido (novo/renovação)",
                  field2: "País do passaporte",
                  field3: "Urgência / prazo",
                }}
              />
            }
          />
          <Route
            path="/flight"
            element={
              <GenericServicePage
                serviceType="flight"
                i18nKey="flight"
                testIdPrefix="flight"
                fieldLabels={{
                  field1: "Cidade de partida",
                  field2: "Cidade de destino",
                  field3: "Datas (ida/volta)",
                }}
              />
            }
          />
          <Route
            path="/insurance"
            element={
              <GenericServicePage
                serviceType="insurance"
                i18nKey="insurance"
                testIdPrefix="insurance"
                fieldLabels={{
                  field1: "Destino(s)",
                  field2: "Datas da viagem",
                  field3: "Número de viajantes",
                }}
              />
            }
          />
          <Route
            path="/admin/requests"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]} loginPath="/portal/login" redirect="/account">
                <AdminRequestsPage />
              </ProtectedRoute>
            }
          />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/apply/visa" element={<VisaCountriesPage />} />
          <Route path="/apply/canada" element={<CanadaApplyPage />} />
          <Route path="/apply/portugal" element={<PortugalApplyPage />} />
          <Route path="/apply/:country" element={<CountryApplyPage />} />
          <Route path="/privacy/my-data" element={<DataRequestPage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/magic" element={<MagicPage />} />

          {/* Internal ERP portal */}
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route
            path="/portal"
            element={
              <ProtectedRoute allowedRoles={["admin", "agent"]} loginPath="/portal/login" redirect="/account">
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PortalDashboard />} />
            <Route path="processes" element={<PortalProcesses />} />
            <Route path="processes/:id" element={<PortalProcessDetail />} />
            <Route path="applications" element={<PortalApplications />} />
            <Route path="applications/:id" element={<PortalApplications />} />
            <Route path="users" element={<PortalUsers />} />
            <Route path="billing" element={<PortalBilling />} />
            <Route path="ai" element={<PortalAi />} />
            <Route path="settings" element={<PortalSettings />} />
            <Route path="orders" element={<PortalOrders />} />
            <Route path="notifications" element={<PortalNotifications />} />
            <Route path="support" element={<PortalSupport />} />
            <Route path="profile" element={<ProfilePage area="portal" />} />
          </Route>

          {/* Customer self-service portal */}
          <Route path="/account/login" element={<CustomerLogin />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute allowedRoles={["customer"]} loginPath="/account/login" redirect="/portal">
                <AccountLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<CustomerDashboard />} />
            <Route path="processes/:id" element={<CustomerProcessDetail />} />
            <Route path="billing" element={<CustomerBilling />} />
            <Route path="profile" element={<ProfilePage area="account" />} />
          </Route>
          </Routes>
        <ChatWidget />
        <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </I18nProvider>
  );
}

export default App;
